"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
        desc = { enumerable: true, get: function () { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function (o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function (o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function (o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o)
                if (Object.prototype.hasOwnProperty.call(o, k))
                    ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule)
            return mod;
        var result = {};
        if (mod != null)
            for (var k = ownKeys(mod), i = 0; i < k.length; i++)
                if (k[i] !== "default")
                    __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GroupedMultiSelectMenu = void 0;
const react_1 = __importStar(require("react"));
const ui_components_1 = require("@groupby/ui-components");
const material_1 = require("@mui/material");
const debounce_1 = __importDefault(require("debounce"));
const utils_1 = require("../../utils");
const grouped_menu_content_1 = require("./grouped-menu-content");
const grouped_multi_select_menu_styles_1 = require("./grouped-multi-select-menu.styles");
const client_constants_1 = require("../../client/client.constants");
const loading_indicator_1 = require("../loading-indicator");
const localization_1 = require("../../../localization");
function flattenSelectedOptions(groups, groupedOptions) {
    return groups.flatMap((parent) => {
        if (parent.allSelected && !parent.children) {
            // Find the corresponding group in groupedOptions to get all children
            const fullGroup = groupedOptions.find((option) => option.value === parent.value);
            return (fullGroup === null || fullGroup === void 0 ? void 0 : fullGroup.children) ? fullGroup.children.map((child) => child.value) : [];
        }
        // For partial selections, use the existing children
        return parent.children ? parent.children.map((child) => child.value) : [];
    });
}
const GroupedMultiSelectMenu = ({ anchorEl, anchorPosition, anchorReference, groupedOptions, selected, allowEmptySelection, hasNextPage, moreItemsLoading, isLoading, isDisabled, isSubmitting, isSearching, initialExpandedParentId, onClose, onApply, onCreate, onSearch, onLoadMore, anchorOrigin = { vertical: 'bottom', horizontal: 'left' }, transformOrigin, optimizePayload, // we use this as a flag that we don't want to store children in the localStorage.
//  In future we will use shared state between StrategyTreeL1CategoryDropdown and StrategyMatrix
additionalControls, }) => {
    const { formatMessage: t } = (0, localization_1.useLocalization)();
    const [selectedIds, setSelectedIds] = (0, react_1.useState)(selected ? flattenSelectedOptions(selected, groupedOptions) : []);
    const [searchText, setSearchText] = (0, react_1.useState)('');
    const [expandAllFn, setExpandAllFn] = (0, react_1.useState)(null);
    const [collapseAllFn, setCollapseAllFn] = (0, react_1.useState)(null);
    const [expandedCount, setExpandedCount] = (0, react_1.useState)(0);
    const onSearchDebounced = (0, react_1.useMemo)(() => (0, debounce_1.default)((value) => {
        if (onSearch) {
            onSearch(value);
        }
    }, 250), [onSearch]);
    const wasOpen = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        const isOpen = !!(anchorEl || anchorPosition);
        if (isOpen && !wasOpen.current) {
            // Sync from parent only on fresh open
            setSelectedIds(selected ? flattenSelectedOptions(selected, groupedOptions) : []);
        }
        if (!isOpen) {
            setSearchText('');
        }
        wasOpen.current = isOpen;
    }, [anchorEl, anchorPosition, selected, groupedOptions]);
    const handleSelectChild = (0, react_1.useCallback)((childId, isChecked) => {
        setSelectedIds((prev) => (0, utils_1.updateUniqueItemListBasedOnValue)(childId, isChecked)(prev));
    }, []);
    const allChildIds = (0, react_1.useMemo)(() => groupedOptions.flatMap((group) => (group.children ? group.children.map((child) => child.value) : [])), [groupedOptions]);
    const areAllSelected = selectedIds.length > 0 && selectedIds.length === allChildIds.length;
    const handleSelectAll = () => setSelectedIds(allChildIds);
    const handleClearAll = () => setSelectedIds([]);
    const toggleSelectAll = () => {
        if (areAllSelected) {
            handleClearAll();
        }
        else {
            handleSelectAll();
        }
    };
    const handleToggleExpandAll = () => {
        if (!expandAllFn || !collapseAllFn)
            return;
        // If at least one parent is expanded, we collapse all. If none expanded, we expand.
        if (expandedCount > 0) {
            collapseAllFn();
        }
        else {
            expandAllFn();
        }
    };
    const handleApply = () => {
        const selectedStructure = groupedOptions
            .map((parent) => {
            var _a;
            const selectedChildren = parent.children ? parent.children.filter((child) => selectedIds.includes(child.value)) : [];
            const allSelected = selectedChildren.length === ((_a = parent.children) === null || _a === void 0 ? void 0 : _a.length) && selectedChildren.length > 0;
            // Control behavior based on optimizePayload flag
            const children = optimizePayload && allSelected ? undefined : selectedChildren;
            return Object.assign(Object.assign({}, parent), { children, allSelected });
        })
            .filter((parent) => parent.allSelected || (parent.children && parent.children.length > 0));
        onApply(selectedStructure);
        onClose();
    };
    const handleSearchTextChange = (searchText) => {
        setSearchText(searchText);
        if (onSearch) {
            if (searchText.length < client_constants_1.MIN_SEARCH_CHARACTERS_ALLOWED) {
                onSearchDebounced('');
            }
            else {
                onSearchDebounced(searchText);
            }
        }
    };
    const displayedGroups = groupedOptions;
    const isNoneSelected = selectedIds.length === 0;
    const isCompletelyEmpty = displayedGroups.length === 0;
    const areActionsDisabled = isDisabled
        || isLoading
        || (isCompletelyEmpty && !onCreate)
        || (!allowEmptySelection && isNoneSelected);
    const isAtLeastOneExpanded = expandedCount > 0;
    const expandCollapseLabel = isAtLeastOneExpanded
        ? t({ key: 'COLLAPSE_ALL' })
        : t({ key: 'EXPAND_ALL' });
    const selectAllLabel = areAllSelected
        ? t({ key: 'CLEAR_SELECTION' })
        : t({ key: 'SELECT_ALL' });
    return (react_1.default.createElement(material_1.Popover, { open: !!anchorEl || !!anchorPosition, anchorReference: anchorReference, anchorEl: anchorEl, anchorPosition: anchorPosition, onClose: onClose, TransitionProps: { exit: false, unmountOnExit: true }, anchorOrigin: anchorOrigin, transformOrigin: transformOrigin }, react_1.default.createElement(grouped_multi_select_menu_styles_1.StyledContainer, null, additionalControls, react_1.default.createElement(ui_components_1.SearchField, { value: searchText, onChange: handleSearchTextChange, placeholder: t({ key: 'SEARCH' }), InputProps: {
            startAdornment: (react_1.default.createElement(material_1.InputAdornment, { position: "start" }, react_1.default.createElement(ui_components_1.Icon, { variant: "search", color: ui_components_1.baseColors.primaryGreen400, className: "search-icon" }))),
        } }), !isDisabled && (react_1.default.createElement("div", { className: "actions" }, react_1.default.createElement(ui_components_1.Button, { rounded: true, size: "small", title: expandCollapseLabel, onClick: handleToggleExpandAll }), react_1.default.createElement(ui_components_1.Button, { rounded: true, size: "small", title: selectAllLabel, onClick: toggleSelectAll }))), react_1.default.createElement(grouped_menu_content_1.GroupedMenuContent, { options: displayedGroups, selectedIds: selectedIds, isEmpty: isCompletelyEmpty, isDisabled: isDisabled, isLoading: isLoading, isSearching: isSearching, moreItemsLoading: moreItemsLoading, hasNextPage: hasNextPage, initialExpandedParentId: initialExpandedParentId, onLoadMore: onLoadMore, onSelect: handleSelectChild, registerExpandMethods: (expandAll, collapseAll) => {
            setExpandAllFn(() => expandAll);
            setCollapseAllFn(() => collapseAll);
        }, onExpandCountChange: (count) => setExpandedCount(count) }), react_1.default.createElement(material_1.Divider, null), react_1.default.createElement("div", { className: "footer" }, react_1.default.createElement(ui_components_1.Button, { rounded: true, color: "primary", title: t({ key: 'CANCEL' }), onClick: onClose }), react_1.default.createElement(ui_components_1.Button, { rounded: true, color: "primary", title: t({ key: 'APPLY' }), variant: "contained", disabled: areActionsDisabled || isSubmitting, onClick: handleApply, endIcon: isSubmitting && react_1.default.createElement(loading_indicator_1.LoadingIndicator, null) })))));
};
exports.GroupedMultiSelectMenu = GroupedMultiSelectMenu;
exports.default = exports.GroupedMultiSelectMenu;
